import React, { forwardRef, ReactNode } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Box, IconButton } from "@mui/material";
import { X } from "lucide-react";

interface BaseModalProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
  isOpen: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  dialogProps?: {
    PaperProps?: {
      sx?: Record<string, any>;
    };
    [key: string]: any;
  };
  customStyles?: Record<string, any>;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#2D2D2D",
    color: "#FFFFFF",
    border: "2px solid #1BCF66",
    borderRadius: 16,
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: "#2D2D2D",
  color: "#1BCF66",
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: "#2D2D2D",
  color: "#FFFFFF",
  padding: theme.spacing(2),

  msOverflowStyle: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },

  "& > *": {
    "&::-webkit-scrollbar": {
      width: "7px",
      height: "7px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#30303000",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgb(70, 70, 70)",
      borderRadius: "8px",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "rgb(70, 70, 70) transparent",
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor: "#2D2D2D",
  padding: theme.spacing(2),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "#999999",
  padding: 8,
  "&:hover": {
    color: "#FFFFFF",
    backgroundColor: "rgba(153, 153, 153, 0.1)",
  },
}));

export const BaseModal = forwardRef<HTMLDivElement, BaseModalProps>(
  (
    {
      title,
      children,
      onClose,
      isOpen,
      maxWidth = "sm",
      fullWidth = true,
      dialogProps = {},
      customStyles = {},
    },
    ref
  ) => {
    return (
      <StyledDialog
        ref={ref}
        open={Boolean(isOpen)}
        onClose={onClose}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        {...dialogProps}
        PaperProps={{
          ...dialogProps.PaperProps,
          sx: {
            ...(dialogProps.PaperProps?.sx ?? {}),
            ...customStyles,
          },
        }}
      >
        <StyledDialogTitle>
          <Box component="span">{title}</Box>
          <CloseButton onClick={onClose} size="small">
            <X size={20} />
          </CloseButton>
        </StyledDialogTitle>
        <StyledDialogContent>{children}</StyledDialogContent>
        <StyledDialogActions></StyledDialogActions>
      </StyledDialog>
    );
  }
);

BaseModal.displayName = "BaseModal";
